import React, { useState, useRef, Fragment, createElement } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { MaternityFile } from '../../types';
import { Search, FileText, Printer, Download, Baby, X, Trash2 } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
async function captureToA4Pdf(element: HTMLElement, filename: string) {
  const PAGE_W_MM = 210;
  const PAGE_H_MM = 297;
  const MARGIN_TOP_MM = 20;
  const MARGIN_BOTTOM_MM = 20;
  const MARGIN_LEFT_MM = 18;
  const MARGIN_RIGHT_MM = 18;
  const CONTENT_W_MM = PAGE_W_MM - MARGIN_LEFT_MM - MARGIN_RIGHT_MM;
  const CONTENT_H_MM = PAGE_H_MM - MARGIN_TOP_MM - MARGIN_BOTTOM_MM;
  const sourceCanvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    logging: false
  });
  const pdf = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait',
    compress: true
  });
  const sourceW = sourceCanvas.width;
  const sourceH = sourceCanvas.height;
  const pxPerMm = sourceW / CONTENT_W_MM;
  const pageSlicePx = Math.floor(CONTENT_H_MM * pxPerMm);
  let cursorPx = 0;
  let pageIndex = 0;
  while (cursorPx < sourceH) {
    const remainingPx = sourceH - cursorPx;
    const sliceHpx = Math.min(pageSlicePx, remainingPx);
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = sourceW;
    sliceCanvas.height = sliceHpx;
    const ctx = sliceCanvas.getContext('2d');
    if (!ctx) break;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, sourceW, sliceHpx);
    ctx.drawImage(sourceCanvas, 0, cursorPx, sourceW, sliceHpx, 0, 0, sourceW, sliceHpx);
    const sliceImg = sliceCanvas.toDataURL('image/jpeg', 0.95);
    const sliceHmm = sliceHpx / pxPerMm;
    if (pageIndex > 0) pdf.addPage();
    pdf.addImage(sliceImg, 'JPEG', MARGIN_LEFT_MM, MARGIN_TOP_MM, CONTENT_W_MM, sliceHmm);
    cursorPx += sliceHpx;
    pageIndex += 1;
  }
  pdf.save(filename);
}
export const MaternityFiles: React.FC = () => {
  const {
    maternityFiles,
    deleteMaternityFile,
    currentUser
  } = useData();
  const isAdmin = currentUser?.role === 'Administrator';
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFile, setSelectedFile] = useState<MaternityFile | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const printableRef = useRef<HTMLDivElement>(null);
  const handleDeleteFile = (file: MaternityFile) => {
    if (window.confirm(`WARNING: This will permanently delete the maternity file for ${file.patient.firstName} ${file.patient.surname} (${file.bmcFileNumber}) and ALL of its records. This cannot be undone. Are you sure?`)) {
      deleteMaternityFile(file.id);
      setSelectedFile(null);
    }
  };
  const filteredFiles = maternityFiles.filter(m => {
    const q = searchQuery.toLowerCase();
    return m.patient.firstName.toLowerCase().includes(q) || m.patient.surname.toLowerCase().includes(q) || m.bmcFileNumber.toLowerCase().includes(q);
  }).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  const handlePrint = () => {
    document.body.classList.add('printing-maternity-modal');
    window.print();
    setTimeout(() => {
      document.body.classList.remove('printing-maternity-modal');
    }, 500);
  };
  const handleDownloadPdf = async () => {
    if (!printableRef.current || !selectedFile) return;
    setIsGeneratingPdf(true);
    try {
      const filename = `BMED_Maternity_${selectedFile.bmcFileNumber}_${selectedFile.patient.firstName}_${selectedFile.patient.surname}.pdf`;
      await captureToA4Pdf(printableRef.current, filename);
    } catch (err) {
      console.error('PDF generation failed', err);
      alert('Failed to generate PDF.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };
  return <div className="space-y-6" data-id="element-2578">
      <div className="flex justify-between items-center print:hidden" data-id="element-2579">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-2580">
          Maternity Patient Files
        </h1>
      </div>

      {!selectedFile ? <Card noPadding data-id="element-2581">
          <div className="p-4 border-b border-gray-200 bg-gray-50" data-id="element-2582">
            <div className="relative max-w-md" data-id="element-2583">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2584">
                <Search className="h-5 w-5 text-gray-400" data-id="element-2585" />
              </div>
              <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-pink-500 focus:border-pink-500 sm:text-sm" placeholder="Search by name or BMC file number..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-2586" />
            </div>
          </div>
          <ul className="divide-y divide-gray-200" data-id="element-2587">
            {filteredFiles.length > 0 ? filteredFiles.map(file => <li key={file.id} className="p-4 hover:bg-gray-50 flex justify-between items-center" data-id="element-2588">
                  <div className="flex items-center" data-id="element-2589">
                    <div className="bg-pink-100 p-2 rounded-full mr-4" data-id="element-2590">
                      <Baby className="h-6 w-6 text-pink-600" data-id="element-2591" />
                    </div>
                    <div data-id="element-2592">
                      <p className="text-sm font-medium text-blue-600" data-id="element-2593">
                        {file.bmcFileNumber}
                      </p>
                      <p className="text-lg font-bold text-gray-900" data-id="element-2594">
                        {file.patient.firstName} {file.patient.surname}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-2595">
                        Registered:{' '}
                        {new Date(file.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4" data-id="element-2596">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${file.stage === 'COMPLETED' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-2597">
                      {file.stage === 'COMPLETED' ? '✓ Completed' : 'Active (Nursing)'}
                    </span>
                    <Button onClick={() => setSelectedFile(file)} data-id="element-2598">
                      <FileText className="w-4 h-4 mr-2" data-id="element-2599" /> View File
                    </Button>
                    {isAdmin && <Button variant="danger" onClick={() => handleDeleteFile(file)} data-id="element-2600">
                        <Trash2 className="w-4 h-4 mr-2" data-id="element-2601" /> Delete
                      </Button>}
                  </div>
                </li>) : <li className="p-12 text-center text-gray-500" data-id="element-2602">
                <Baby className="mx-auto h-12 w-12 text-gray-300 mb-4" data-id="element-2603" />
                <p className="text-lg font-medium text-gray-900" data-id="element-2604">
                  No maternity files found
                </p>
              </li>}
          </ul>
        </Card> : <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 print:p-0 print:bg-white" data-id="element-2605">
          <div className="bg-white rounded-lg shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-y-auto print:max-h-none print:max-w-none print:shadow-none print:rounded-none print:overflow-visible maternity-modal-print" data-id="element-2606">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-3 flex justify-between items-center z-10 print:hidden" data-id="element-2607">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-2608">
                Maternity File — {selectedFile.bmcFileNumber}
              </h3>
              <div className="flex items-center space-x-2" data-id="element-2609">
                <Button variant="outline" size="sm" onClick={handlePrint} data-id="element-2610">
                  <Printer className="w-4 h-4 mr-1" data-id="element-2611" /> Print
                </Button>
                <Button variant="primary" size="sm" onClick={handleDownloadPdf} disabled={isGeneratingPdf} data-id="element-2612">
                  <Download className="w-4 h-4 mr-1" data-id="element-2613" />{' '}
                  {isGeneratingPdf ? 'Generating...' : 'Download PDF'}
                </Button>
                {isAdmin && <Button variant="danger" size="sm" onClick={() => handleDeleteFile(selectedFile)} data-id="element-2614">
                    <Trash2 className="w-4 h-4 mr-1" data-id="element-2615" /> Delete
                  </Button>}
                <button onClick={() => setSelectedFile(null)} className="p-1.5 hover:bg-gray-100 rounded" data-id="element-2616">
                  <X className="w-5 h-5 text-gray-500" data-id="element-2617" />
                </button>
              </div>
            </div>

            <div ref={printableRef} className="p-8 bg-white text-sm font-sans text-black" data-id="element-2618">
              {/* Header */}
              <div className="flex justify-between items-start mb-8 border-b-2 border-black pb-4" data-id="element-2619">
                <div className="flex items-center" data-id="element-2620">
                  <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED" className="h-16 w-16 object-contain mr-3" data-id="element-2621" />
                  <div data-id="element-2622">
                    <h1 className="text-xl font-bold uppercase" data-id="element-2623">
                      BMED Medical Centre
                    </h1>
                    <p className="text-xs italic" data-id="element-2624">Maternity Care Made Better</p>
                  </div>
                </div>
                <div className="text-right" data-id="element-2625">
                  <h2 className="text-lg font-bold uppercase tracking-widest border-b border-black pb-1 mb-2" data-id="element-2626">
                    Maternity Record
                  </h2>
                  <p data-id="element-2627">
                    <span className="font-semibold" data-id="element-2628">File No:</span>{' '}
                    {selectedFile.bmcFileNumber}
                  </p>
                  <p data-id="element-2629">
                    <span className="font-semibold" data-id="element-2630">Registered:</span>{' '}
                    {new Date(selectedFile.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>

              {/* PATIENT'S DETAILS */}
              <div className="mb-6" data-id="element-2631">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-2632">
                  PATIENT'S DETAILS
                </h3>
                <div className="grid grid-cols-2 gap-y-2 gap-x-8" data-id="element-2633">
                  <div data-id="element-2634">
                    <span className="font-semibold" data-id="element-2635">First Name:</span>{' '}
                    {selectedFile.patient.firstName}
                  </div>
                  <div data-id="element-2636">
                    <span className="font-semibold" data-id="element-2637">Surname:</span>{' '}
                    {selectedFile.patient.surname}
                  </div>
                  <div data-id="element-2638">
                    <span className="font-semibold" data-id="element-2639">Other Names:</span>{' '}
                    {selectedFile.patient.otherNames}
                  </div>
                  <div data-id="element-2640">
                    <span className="font-semibold" data-id="element-2641">Age:</span>{' '}
                    {selectedFile.patient.age}
                  </div>
                  <div data-id="element-2642">
                    <span className="font-semibold" data-id="element-2643">Religion:</span>{' '}
                    {selectedFile.patient.religion}
                  </div>
                  <div data-id="element-2644">
                    <span className="font-semibold" data-id="element-2645">Church Attended:</span>{' '}
                    {selectedFile.patient.churchAttended}
                  </div>
                  <div className="col-span-2" data-id="element-2646">
                    <span className="font-semibold" data-id="element-2647">Residential Address:</span>{' '}
                    {selectedFile.patient.residentialAddress}
                  </div>
                  <div className="col-span-2" data-id="element-2648">
                    <span className="font-semibold" data-id="element-2649">Location/Landmark:</span>{' '}
                    {selectedFile.patient.locationLandmark}
                  </div>
                </div>
              </div>

              {/* SPOUSE DETAILS */}
              <div className="mb-6" data-id="element-2650">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-2651">
                  SPOUSE DETAILS (OPTIONAL)
                </h3>
                <div className="grid grid-cols-2 gap-y-2 gap-x-8" data-id="element-2652">
                  <div data-id="element-2653">
                    <span className="font-semibold" data-id="element-2654">First Name:</span>{' '}
                    {selectedFile.spouse.firstName}
                  </div>
                  <div data-id="element-2655">
                    <span className="font-semibold" data-id="element-2656">Surname:</span>{' '}
                    {selectedFile.spouse.surname}
                  </div>
                  <div data-id="element-2657">
                    <span className="font-semibold" data-id="element-2658">Other Names:</span>{' '}
                    {selectedFile.spouse.otherNames}
                  </div>
                  <div data-id="element-2659">
                    <span className="font-semibold" data-id="element-2660">Occupation:</span>{' '}
                    {selectedFile.spouse.occupation}
                  </div>
                  <div className="col-span-2" data-id="element-2661">
                    <span className="font-semibold" data-id="element-2662">Contact:</span>{' '}
                    {selectedFile.spouse.contact}
                  </div>
                </div>
              </div>

              {/* ADMISSION */}
              <div className="mb-6" data-id="element-2663">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-2664">
                  ADMISSION
                </h3>
                <div className="flex space-x-16" data-id="element-2665">
                  <div data-id="element-2666">
                    <span className="font-semibold" data-id="element-2667">Date:</span>{' '}
                    {selectedFile.admission.date}
                  </div>
                  <div data-id="element-2668">
                    <span className="font-semibold" data-id="element-2669">Time of Admission:</span>{' '}
                    {selectedFile.admission.time}
                  </div>
                </div>
              </div>

              {/* GENERAL EXAMINATION */}
              <div className="mb-6" data-id="element-2670">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-2671">
                  GENERAL EXAMINATION
                </h3>
                <div className="space-y-2" data-id="element-2672">
                  <div data-id="element-2673">
                    <span className="font-semibold" data-id="element-2674">
                      Cardiovascular System:
                    </span>{' '}
                    {selectedFile.generalExam.cardiovascularSystem}
                  </div>
                  <div className="flex space-x-8" data-id="element-2675">
                    <div data-id="element-2676">
                      <span className="font-semibold" data-id="element-2677">Temp:</span>{' '}
                      {selectedFile.generalExam.temp}
                    </div>
                    <div data-id="element-2678">
                      <span className="font-semibold" data-id="element-2679">Pulse:</span>{' '}
                      {selectedFile.generalExam.pulse}
                    </div>
                    <div data-id="element-2680">
                      <span className="font-semibold" data-id="element-2681">Respiration:</span>{' '}
                      {selectedFile.generalExam.respiration}
                    </div>
                    <div data-id="element-2682">
                      <span className="font-semibold" data-id="element-2683">BP:</span>{' '}
                      {selectedFile.generalExam.bp}
                    </div>
                  </div>
                  <div className="flex space-x-16" data-id="element-2684">
                    <div data-id="element-2685">
                      <span className="font-semibold" data-id="element-2686">Oedema:</span>{' '}
                      {selectedFile.generalExam.oedema}
                    </div>
                    <div data-id="element-2687">
                      <span className="font-semibold" data-id="element-2688">Anemia:</span>{' '}
                      {selectedFile.generalExam.anemia}
                    </div>
                  </div>
                  <div data-id="element-2689">
                    <span className="font-semibold" data-id="element-2690">Respiratory System:</span>{' '}
                    {selectedFile.generalExam.respiratorySystem}
                  </div>
                  <div data-id="element-2691">
                    <span className="font-semibold" data-id="element-2692">
                      Central Nervous System:
                    </span>{' '}
                    {selectedFile.generalExam.centralNervousSystem}
                  </div>
                  <div className="flex space-x-8" data-id="element-2693">
                    <div data-id="element-2694">
                      <span className="font-semibold" data-id="element-2695">Breasts:</span>{' '}
                      {selectedFile.generalExam.breasts}
                    </div>
                    <div data-id="element-2696">
                      <span className="font-semibold" data-id="element-2697">Varicosities:</span>{' '}
                      {selectedFile.generalExam.varicosities}
                    </div>
                    <div data-id="element-2698">
                      <span className="font-semibold" data-id="element-2699">Vaginal Discharge:</span>{' '}
                      {selectedFile.generalExam.vaginalDischarge}
                    </div>
                  </div>
                  <div data-id="element-2700">
                    <span className="font-semibold" data-id="element-2701">Other Abnormalities:</span>{' '}
                    {selectedFile.generalExam.otherAbnormalities}
                  </div>
                  <div className="flex space-x-8 mt-2" data-id="element-2702">
                    <div data-id="element-2703">
                      <span className="font-semibold" data-id="element-2704">MS Urine: SG:</span>{' '}
                      {selectedFile.generalExam.msUrine.sg}
                    </div>
                    <div data-id="element-2705">
                      <span className="font-semibold" data-id="element-2706">Albumen:</span>{' '}
                      {selectedFile.generalExam.msUrine.albumen}
                    </div>
                    <div data-id="element-2707">
                      <span className="font-semibold" data-id="element-2708">Acetone:</span>{' '}
                      {selectedFile.generalExam.msUrine.acetone}
                    </div>
                    <div data-id="element-2709">
                      <span className="font-semibold" data-id="element-2710">Sugar:</span>{' '}
                      {selectedFile.generalExam.msUrine.sugar}
                    </div>
                  </div>
                  <div className="flex space-x-8" data-id="element-2711">
                    <div data-id="element-2712">
                      <span className="font-semibold" data-id="element-2713">Hb%:</span>{' '}
                      {selectedFile.generalExam.hb}{' '}
                      <span className="italic text-xs" data-id="element-2714">
                        (NOTE: Refer to UTH if HB% is 6grams or below)
                      </span>
                    </div>
                    <div data-id="element-2715">
                      <span className="font-semibold" data-id="element-2716">ABO:</span>{' '}
                      {selectedFile.generalExam.abo}
                    </div>
                    <div data-id="element-2717">
                      <span className="font-semibold" data-id="element-2718">Rh:</span>{' '}
                      {selectedFile.generalExam.rh}
                    </div>
                  </div>
                </div>
              </div>

              {/* PRESENT PREGNANCY */}
              <div className="mb-6" data-id="element-2719">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-2720">
                  PRESENT PREGNANCY
                </h3>
                <div className="space-y-2" data-id="element-2721">
                  <div className="flex space-x-8" data-id="element-2722">
                    <div data-id="element-2723">
                      <span className="font-semibold" data-id="element-2724">LMP:</span>{' '}
                      {selectedFile.presentPregnancy.lmp}
                    </div>
                    <div data-id="element-2725">
                      <span className="font-semibold" data-id="element-2726">Quickening:</span>{' '}
                      {selectedFile.presentPregnancy.quickening}
                    </div>
                    <div data-id="element-2727">
                      <span className="font-semibold" data-id="element-2728">EDD:</span>{' '}
                      {selectedFile.presentPregnancy.edd}
                    </div>
                    <div data-id="element-2729">
                      <span className="font-semibold" data-id="element-2730">Gestation Age:</span>{' '}
                      {selectedFile.presentPregnancy.gestationAge}
                    </div>
                  </div>
                  <div className="flex space-x-8" data-id="element-2731">
                    <div data-id="element-2732">
                      <span className="font-semibold" data-id="element-2733">
                        Summary of ANC Findings 1st BP:
                      </span>{' '}
                      {selectedFile.presentPregnancy.ancSummaryFirstBp}
                    </div>
                    <div data-id="element-2734">
                      <span className="font-semibold" data-id="element-2735">at:</span>{' '}
                      {selectedFile.presentPregnancy.ancSummaryAtWeeks} Weeks
                    </div>
                  </div>
                  <div className="flex space-x-8" data-id="element-2736">
                    <div data-id="element-2737">
                      <span className="font-semibold" data-id="element-2738">
                        Most recent Syphilis Test Date:
                      </span>{' '}
                      {selectedFile.presentPregnancy.syphilisTest.date}
                    </div>
                    <div data-id="element-2739">
                      <span className="font-semibold" data-id="element-2740">Result:</span>{' '}
                      {selectedFile.presentPregnancy.syphilisTest.result}
                    </div>
                    <div data-id="element-2741">
                      <span className="font-semibold" data-id="element-2742">Treatment:</span>{' '}
                      {selectedFile.presentPregnancy.syphilisTest.treatment}
                    </div>
                  </div>
                  <div className="flex space-x-8" data-id="element-2743">
                    <div data-id="element-2744">
                      <span className="font-semibold" data-id="element-2745">
                        Most recent HP Test Date:
                      </span>{' '}
                      {selectedFile.presentPregnancy.hpTest.date}
                    </div>
                    <div data-id="element-2746">
                      <span className="font-semibold" data-id="element-2747">Result:</span>{' '}
                      {selectedFile.presentPregnancy.hpTest.result}
                    </div>
                  </div>
                  <div data-id="element-2748">
                    <span className="font-semibold" data-id="element-2749">Pelvic Assessment:</span>{' '}
                    {selectedFile.presentPregnancy.pelvicAssessment}
                  </div>
                  <div data-id="element-2750">
                    <span className="font-semibold" data-id="element-2751">Abnormalities:</span>{' '}
                    {selectedFile.presentPregnancy.abnormalities}
                  </div>
                </div>
              </div>

              {/* OBSERVATIONS FOR LATENT PHASE LABOUR */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-2752">
                <h3 className="font-bold uppercase underline mb-1" data-id="element-2753">
                  OBSERVATIONS FOR LATENT PHASE LABOUR
                </h3>
                <p className="text-xs italic mb-2" data-id="element-2754">
                  NOTE: Refer to UTH if latest phase exceeds 8 hours
                </p>
                <table className="w-full text-xs border-collapse border border-black text-center" data-id="element-2755">
                  <thead data-id="element-2756">
                    <tr className="bg-gray-100" data-id="element-2757">
                      <th className="border border-black p-1" data-id="element-2758">Date</th>
                      <th className="border border-black p-1" data-id="element-2759">Time</th>
                      <th className="border border-black p-1" data-id="element-2760">Pulse</th>
                      <th className="border border-black p-1" data-id="element-2761">BP</th>
                      <th className="border border-black p-1" data-id="element-2762">FH</th>
                      <th className="border border-black p-1" data-id="element-2763">Temp</th>
                      <th className="border border-black p-1" data-id="element-2764">Urinalysis</th>
                      <th className="border border-black p-1" data-id="element-2765">Contractions</th>
                      <th className="border border-black p-1" data-id="element-2766">
                        Cervical Dilation
                      </th>
                      <th className="border border-black p-1" data-id="element-2767">
                        Membranes Liquor
                      </th>
                      <th className="border border-black p-1" data-id="element-2768">Remarks</th>
                      <th className="border border-black p-1" data-id="element-2769">Sign</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-2770">
                    {selectedFile.labourObservations.length > 0 ? selectedFile.labourObservations.map(obs => <tr key={obs.id} data-id="element-2771">
                          <td className="border border-black p-1" data-id="element-2772">
                            {obs.date}
                          </td>
                          <td className="border border-black p-1" data-id="element-2773">
                            {obs.time}
                          </td>
                          <td className="border border-black p-1" data-id="element-2774">
                            {obs.pulse}
                          </td>
                          <td className="border border-black p-1" data-id="element-2775">{obs.bp}</td>
                          <td className="border border-black p-1" data-id="element-2776">{obs.fh}</td>
                          <td className="border border-black p-1" data-id="element-2777">
                            {obs.temp}
                          </td>
                          <td className="border border-black p-1" data-id="element-2778">
                            {obs.urinalysis}
                          </td>
                          <td className="border border-black p-1" data-id="element-2779">
                            {obs.contractions}
                          </td>
                          <td className="border border-black p-1" data-id="element-2780">
                            {obs.cervicalDilation}
                          </td>
                          <td className="border border-black p-1" data-id="element-2781">
                            {obs.membranesLiquor}
                          </td>
                          <td className="border border-black p-1 text-left" data-id="element-2782">
                            {obs.remarks}
                          </td>
                          <td className="border border-black p-1" data-id="element-2783">
                            {obs.sign}
                          </td>
                        </tr>) : <tr data-id="element-2784">
                        <td colSpan={12} className="border border-black p-4 text-gray-400 italic" data-id="element-2785">
                          No observations recorded
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>

              {/* DESCRIPTION OF LABOUR AND DELIVERY */}
              <div className="mb-6" data-id="element-2786">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-2787">
                  DESCRIPTION OF LABOUR AND DELIVERY
                </h3>
                <div className="grid grid-cols-2 gap-x-8 gap-y-2" data-id="element-2788">
                  <div data-id="element-2789">
                    <span className="font-semibold" data-id="element-2790">Laceration:</span>{' '}
                    {selectedFile.delivery.laceration} |{' '}
                    <span className="font-semibold" data-id="element-2791">Anterior:</span>{' '}
                    {selectedFile.delivery.anterior} |{' '}
                    <span className="font-semibold" data-id="element-2792">Posterior:</span>{' '}
                    {selectedFile.delivery.posterior} |{' '}
                    <span className="font-semibold" data-id="element-2793">None:</span>{' '}
                    {selectedFile.delivery.none}
                  </div>
                  <div data-id="element-2794">
                    <span className="font-semibold" data-id="element-2795">Repaired:</span>{' '}
                    {selectedFile.delivery.repaired}
                  </div>
                  <div data-id="element-2796">
                    <span className="font-semibold" data-id="element-2797">Membranes:</span>{' '}
                    {selectedFile.delivery.membranes}
                  </div>
                  <div data-id="element-2798">
                    <span className="font-semibold" data-id="element-2799">Date:</span>{' '}
                    {selectedFile.delivery.membranesDate}{' '}
                    <span className="font-semibold ml-4" data-id="element-2800">Time:</span>{' '}
                    {selectedFile.delivery.membranesTime}
                  </div>
                  <div data-id="element-2801">
                    <span className="font-semibold" data-id="element-2802">Episiotomy:</span>{' '}
                    {selectedFile.delivery.episiotomy}
                  </div>
                  <div data-id="element-2803">
                    <span className="font-semibold" data-id="element-2804">Repaired:</span>{' '}
                    {selectedFile.delivery.episiotomyRepaired}
                  </div>
                  <div data-id="element-2805">
                    <span className="font-semibold" data-id="element-2806">Maternal ARV:</span>{' '}
                    {selectedFile.delivery.maternalARV}
                  </div>
                  <div data-id="element-2807">
                    <span className="font-semibold" data-id="element-2808">
                      Which ones (NPV, AZT, OTHER):
                    </span>{' '}
                    {selectedFile.delivery.arvTypes}
                  </div>
                  <div className="col-span-2 flex space-x-8" data-id="element-2809">
                    <div data-id="element-2810">
                      <span className="font-semibold" data-id="element-2811">Uterine massage:</span>{' '}
                      {selectedFile.delivery.uterineMassage}
                    </div>
                    <div data-id="element-2812">
                      <span className="font-semibold" data-id="element-2813">
                        Mother Already on HAART:
                      </span>{' '}
                      {selectedFile.delivery.motherOnHAART}
                    </div>
                    <div data-id="element-2814">
                      <span className="font-semibold" data-id="element-2815">Cytotec:</span>{' '}
                      {selectedFile.delivery.cytotec}
                    </div>
                    <div data-id="element-2816">
                      <span className="font-semibold" data-id="element-2817">Syntometrine:</span>{' '}
                      {selectedFile.delivery.syntometrine}
                    </div>
                  </div>
                  <div data-id="element-2818">
                    <span className="font-semibold" data-id="element-2819">1st stage began Date:</span>{' '}
                    {selectedFile.delivery.firstStageBeganDate}{' '}
                    <span className="font-semibold ml-4" data-id="element-2820">Time:</span>{' '}
                    {selectedFile.delivery.firstStageBeganTime}
                  </div>
                  <div data-id="element-2821">
                    <span className="font-semibold" data-id="element-2822">Placenta Delivery:</span>{' '}
                    {selectedFile.delivery.placentaDelivery}
                  </div>
                  <div data-id="element-2823">
                    <span className="font-semibold" data-id="element-2824">
                      Complete dilation Date:
                    </span>{' '}
                    {selectedFile.delivery.completeDilationDate}{' '}
                    <span className="font-semibold ml-4" data-id="element-2825">Time:</span>{' '}
                    {selectedFile.delivery.completeDilationTime}
                  </div>
                  <div data-id="element-2826">
                    <span className="font-semibold" data-id="element-2827">
                      Placenta Delivered Date:
                    </span>{' '}
                    {selectedFile.delivery.placentaDeliveredDate}{' '}
                    <span className="font-semibold ml-4" data-id="element-2828">Time:</span>{' '}
                    {selectedFile.delivery.placentaDeliveredTime}
                  </div>
                  <div className="col-span-2" data-id="element-2829">
                    <span className="font-semibold" data-id="element-2830">
                      Abnormality at Dilation:
                    </span>{' '}
                    {selectedFile.delivery.abnormalityAtDilation} |{' '}
                    <span className="font-semibold" data-id="element-2831">Describe:</span>{' '}
                    {selectedFile.delivery.abnormalityAtDilationDesc}
                  </div>
                  <div data-id="element-2832">
                    <span className="font-semibold" data-id="element-2833">
                      Postpartum hemorrhage:
                    </span>{' '}
                    {selectedFile.delivery.postpartumHemorrhage}
                  </div>
                  <div data-id="element-2834">
                    <span className="font-semibold" data-id="element-2835">Delivery Method:</span>{' '}
                    {selectedFile.delivery.deliveryMethod}
                  </div>
                  <div data-id="element-2836">
                    <span className="font-semibold" data-id="element-2837">EBL:</span>{' '}
                    {selectedFile.delivery.ebl} cc
                  </div>
                  <div className="col-span-2" data-id="element-2838">
                    <span className="font-semibold" data-id="element-2839">
                      Abnormality at Delivery:
                    </span>{' '}
                    {selectedFile.delivery.abnormalityAtDelivery} |{' '}
                    <span className="font-semibold" data-id="element-2840">Describe:</span>{' '}
                    {selectedFile.delivery.abnormalityAtDeliveryDesc}
                  </div>
                  <div className="col-span-2" data-id="element-2841">
                    <span className="font-semibold" data-id="element-2842">Complications:</span>{' '}
                    {selectedFile.delivery.complications}
                  </div>
                </div>

                <div className="mt-4" data-id="element-2843">
                  <h4 className="font-bold text-sm mb-1" data-id="element-2844">DELIVERY SUMMARY:</h4>
                  <table className="w-full text-xs border-collapse border border-black text-center" data-id="element-2845">
                    <thead data-id="element-2846">
                      <tr className="bg-gray-100" data-id="element-2847">
                        <th className="border border-black p-1" data-id="element-2848">DATE</th>
                        <th className="border border-black p-1" data-id="element-2849">
                          TIME OF BIRTH
                        </th>
                        <th className="border border-black p-1" data-id="element-2850">SEX</th>
                        <th className="border border-black p-1" data-id="element-2851">WEIGHT</th>
                        <th className="border border-black p-1" data-id="element-2852">
                          DELIVERED BY
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-2853">
                      {selectedFile.delivery.deliverySummary.length > 0 ? selectedFile.delivery.deliverySummary.map(sum => <tr key={sum.id} data-id="element-2854">
                            <td className="border border-black p-1" data-id="element-2855">
                              {sum.date}
                            </td>
                            <td className="border border-black p-1" data-id="element-2856">
                              {sum.timeOfBirth}
                            </td>
                            <td className="border border-black p-1" data-id="element-2857">
                              {sum.sex}
                            </td>
                            <td className="border border-black p-1" data-id="element-2858">
                              {sum.weight}
                            </td>
                            <td className="border border-black p-1" data-id="element-2859">
                              {sum.deliveredBy}
                            </td>
                          </tr>) : <tr data-id="element-2860">
                          <td colSpan={5} className="border border-black p-4 text-gray-400 italic" data-id="element-2861">
                            No delivery summary recorded
                          </td>
                        </tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* NEW BORN EVALUATION */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-2862">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-2863">
                  NEW BORN EVALUATION
                </h3>
                <div className="grid grid-cols-2 gap-x-8 gap-y-2" data-id="element-2864">
                  <div data-id="element-2865">
                    <span className="font-semibold" data-id="element-2866">EGA:</span>{' '}
                    {selectedFile.newbornEvaluation.ega}
                  </div>
                  <div data-id="element-2867">
                    <span className="font-semibold" data-id="element-2868">ALIVE:</span>{' '}
                    {selectedFile.newbornEvaluation.alive}
                  </div>
                  <div data-id="element-2869">
                    <span className="font-semibold" data-id="element-2870">Neonatal Death:</span>{' '}
                    {selectedFile.newbornEvaluation.neonatalDeath}
                  </div>
                  <div data-id="element-2871">
                    <span className="font-semibold" data-id="element-2872">
                      If still born or death? Cause:
                    </span>{' '}
                    {selectedFile.newbornEvaluation.causeOfDeath}
                  </div>
                  <div className="col-span-2 flex space-x-8" data-id="element-2873">
                    <div data-id="element-2874">
                      <span className="font-semibold" data-id="element-2875">Apgar 1 Minutes:</span>{' '}
                      {selectedFile.newbornEvaluation.apgar1}
                    </div>
                    <div data-id="element-2876">
                      <span className="font-semibold" data-id="element-2877">Apgar 5 Minutes:</span>{' '}
                      {selectedFile.newbornEvaluation.apgar5}
                    </div>
                    <div data-id="element-2878">
                      <span className="font-semibold" data-id="element-2879">Apgar 10 Minutes:</span>{' '}
                      {selectedFile.newbornEvaluation.apgar10}
                    </div>
                  </div>
                  <div data-id="element-2880">
                    <span className="font-semibold" data-id="element-2881">General Exam:</span>{' '}
                    {selectedFile.newbornEvaluation.generalExam}
                  </div>
                  <div data-id="element-2882">
                    <span className="font-semibold" data-id="element-2883">
                      Congenital Malformation:
                    </span>{' '}
                    {selectedFile.newbornEvaluation.congenitalMalformation}
                  </div>
                  <div className="col-span-2" data-id="element-2884">
                    <span className="font-semibold" data-id="element-2885">If Yes Describe:</span>{' '}
                    {selectedFile.newbornEvaluation.congenitalMalformationDesc}
                  </div>
                  <div data-id="element-2886">
                    <span className="font-semibold" data-id="element-2887">Infant ARVS?:</span>{' '}
                    {selectedFile.newbornEvaluation.infantARVs}
                  </div>
                  <div data-id="element-2888">
                    <span className="font-semibold" data-id="element-2889">Inf Feeding:</span>{' '}
                    {selectedFile.newbornEvaluation.infantFeeding}
                  </div>
                  <div className="col-span-2" data-id="element-2890">
                    <span className="font-semibold" data-id="element-2891">
                      RESUSCITATION METHODS:
                    </span>{' '}
                    {selectedFile.newbornEvaluation.resuscitationMethods}
                  </div>
                </div>

                <div className="mt-4" data-id="element-2892">
                  <h4 className="font-bold text-sm mb-1 underline" data-id="element-2893">
                    NEWBORN EXAMINATION
                  </h4>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-2" data-id="element-2894">
                    <div data-id="element-2895">
                      <span className="font-semibold" data-id="element-2896">CROWN HEEL LENGTH:</span>{' '}
                      {selectedFile.newbornEvaluation.crownHeelLength}
                    </div>
                    <div data-id="element-2897">
                      <span className="font-semibold" data-id="element-2898">HEAD CIRCUMFERENCE:</span>{' '}
                      {selectedFile.newbornEvaluation.headCircumference}
                    </div>
                    <div data-id="element-2899">
                      <span className="font-semibold" data-id="element-2900">Skin: Jaundice:</span>{' '}
                      {selectedFile.newbornEvaluation.skinJaundice}
                    </div>
                    <div data-id="element-2901">
                      <span className="font-semibold" data-id="element-2902">Other:</span>{' '}
                      {selectedFile.newbornEvaluation.skinOther}
                    </div>
                    <div data-id="element-2903">
                      <span className="font-semibold" data-id="element-2904">Fontanelles:</span>{' '}
                      {selectedFile.newbornEvaluation.fontanelles}
                    </div>
                    <div data-id="element-2905">
                      <span className="font-semibold" data-id="element-2906">Eyes:</span>{' '}
                      {selectedFile.newbornEvaluation.eyes} |{' '}
                      <span className="font-semibold" data-id="element-2907">Treatment:</span>{' '}
                      {selectedFile.newbornEvaluation.eyesTreatment}
                    </div>
                    <div data-id="element-2908">
                      <span className="font-semibold" data-id="element-2909">Mouth:</span>{' '}
                      {selectedFile.newbornEvaluation.mouth}
                    </div>
                    <div data-id="element-2910">
                      <span className="font-semibold" data-id="element-2911">Neck:</span>{' '}
                      {selectedFile.newbornEvaluation.neck}
                    </div>
                    <div data-id="element-2912">
                      <span className="font-semibold" data-id="element-2913">Chest:</span>{' '}
                      {selectedFile.newbornEvaluation.chest}
                    </div>
                    <div data-id="element-2914">
                      <span className="font-semibold" data-id="element-2915">Abdomen:</span>{' '}
                      {selectedFile.newbornEvaluation.abdomen}
                    </div>
                    <div data-id="element-2916">
                      <span className="font-semibold" data-id="element-2917">Umbilicus:</span>{' '}
                      {selectedFile.newbornEvaluation.umbilicus}
                    </div>
                    <div data-id="element-2918">
                      <span className="font-semibold" data-id="element-2919">Genitalia:</span>{' '}
                      {selectedFile.newbornEvaluation.genitalia} |{' '}
                      <span className="font-semibold" data-id="element-2920">Urine:</span>{' '}
                      {selectedFile.newbornEvaluation.urine}
                    </div>
                    <div data-id="element-2921">
                      <span className="font-semibold" data-id="element-2922">Anus:</span>{' '}
                      {selectedFile.newbornEvaluation.anus}
                    </div>
                    <div data-id="element-2923">
                      <span className="font-semibold" data-id="element-2924">Meconium:</span>{' '}
                      {selectedFile.newbornEvaluation.meconium}
                    </div>
                    <div data-id="element-2925">
                      <span className="font-semibold" data-id="element-2926">Upper Limbs:</span>{' '}
                      {selectedFile.newbornEvaluation.upperLimbs}
                    </div>
                    <div data-id="element-2927">
                      <span className="font-semibold" data-id="element-2928">ERB Paralysis:</span>{' '}
                      {selectedFile.newbornEvaluation.erbParalysis}
                    </div>
                    <div data-id="element-2929">
                      <span className="font-semibold" data-id="element-2930">Lower Limbs:</span>{' '}
                      {selectedFile.newbornEvaluation.lowerLimbs}
                    </div>
                    <div data-id="element-2931">
                      <span className="font-semibold" data-id="element-2932">Ortolani Sign:</span>{' '}
                      {selectedFile.newbornEvaluation.ortolaniSign}
                    </div>
                    <div className="col-span-2" data-id="element-2933">
                      <span className="font-semibold" data-id="element-2934">Back:</span>{' '}
                      {selectedFile.newbornEvaluation.back}
                    </div>
                    <div className="col-span-2 flex space-x-8" data-id="element-2935">
                      <span className="font-semibold" data-id="element-2936">Reflexes:</span>
                      <div data-id="element-2937">
                        <span className="font-semibold" data-id="element-2938">Grasp:</span>{' '}
                        {selectedFile.newbornEvaluation.reflexesGrasp}
                      </div>
                      <div data-id="element-2939">
                        <span className="font-semibold" data-id="element-2940">Moro (Startle):</span>{' '}
                        {selectedFile.newbornEvaluation.reflexesMoro}
                      </div>
                      <div data-id="element-2941">
                        <span className="font-semibold" data-id="element-2942">Sucking:</span>{' '}
                        {selectedFile.newbornEvaluation.reflexesSucking}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4" data-id="element-2943">
                  <span className="font-semibold" data-id="element-2944">NEWBORN PROGRESS NOTES:</span>
                  <p className="mt-1 whitespace-pre-wrap" data-id="element-2945">
                    {selectedFile.newbornEvaluation.progressNotes}
                  </p>
                </div>
              </div>

              {/* POST DELIVERY OBSERVATION CHART */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-2946">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-2947">
                  POST DELIVERY OBSERVATION CHART
                </h3>
                <table className="w-full text-[10px] border-collapse border border-black text-center" data-id="element-2948">
                  <thead data-id="element-2949">
                    <tr className="bg-gray-100" data-id="element-2950">
                      <th className="border border-black p-1" colSpan={2} data-id="element-2951">
                        TIME
                      </th>
                      <th className="border border-black p-1" colSpan={8} data-id="element-2952">
                        MOTHER
                      </th>
                      <th className="border border-black p-1" colSpan={7} data-id="element-2953">
                        BABY
                      </th>
                      <th className="border border-black p-1" rowSpan={2} data-id="element-2954">
                        SIGN
                      </th>
                    </tr>
                    <tr className="bg-gray-100" data-id="element-2955">
                      <th className="border border-black p-1" data-id="element-2956">DATE</th>
                      <th className="border border-black p-1" data-id="element-2957">TIME</th>
                      <th className="border border-black p-1" data-id="element-2958">BP</th>
                      <th className="border border-black p-1" data-id="element-2959">PULSE</th>
                      <th className="border border-black p-1" data-id="element-2960">RESPS</th>
                      <th className="border border-black p-1" data-id="element-2961">TEMP</th>
                      <th className="border border-black p-1" data-id="element-2962">PV BLEEDING</th>
                      <th className="border border-black p-1" data-id="element-2963">UTERUS</th>
                      <th className="border border-black p-1" data-id="element-2964">BLADDER</th>
                      <th className="border border-black p-1" data-id="element-2965">PALLOR</th>
                      <th className="border border-black p-1" data-id="element-2966">TEMP</th>
                      <th className="border border-black p-1" data-id="element-2967">RESPS</th>
                      <th className="border border-black p-1" data-id="element-2968">CORD</th>
                      <th className="border border-black p-1" data-id="element-2969">SKIN COLOUR</th>
                      <th className="border border-black p-1" data-id="element-2970">
                        FEEDING (BF/AF)
                      </th>
                      <th className="border border-black p-1" data-id="element-2971">BOWEL</th>
                      <th className="border border-black p-1" data-id="element-2972">URINE</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-2973">
                    {selectedFile.postDeliveryObservations.length > 0 ? selectedFile.postDeliveryObservations.map(obs => <tr key={obs.id} data-id="element-2974">
                          <td className="border border-black p-1" data-id="element-2975">
                            {obs.date}
                          </td>
                          <td className="border border-black p-1" data-id="element-2976">
                            {obs.time}
                          </td>
                          <td className="border border-black p-1" data-id="element-2977">{obs.bp}</td>
                          <td className="border border-black p-1" data-id="element-2978">
                            {obs.pulse}
                          </td>
                          <td className="border border-black p-1" data-id="element-2979">
                            {obs.resps}
                          </td>
                          <td className="border border-black p-1" data-id="element-2980">
                            {obs.temp}
                          </td>
                          <td className="border border-black p-1" data-id="element-2981">
                            {obs.pvBleeding}
                          </td>
                          <td className="border border-black p-1" data-id="element-2982">
                            {obs.uterus}
                          </td>
                          <td className="border border-black p-1" data-id="element-2983">
                            {obs.bladder}
                          </td>
                          <td className="border border-black p-1" data-id="element-2984">
                            {obs.pallor}
                          </td>
                          <td className="border border-black p-1" data-id="element-2985">
                            {obs.babyTemp}
                          </td>
                          <td className="border border-black p-1" data-id="element-2986">
                            {obs.babyResps}
                          </td>
                          <td className="border border-black p-1" data-id="element-2987">
                            {obs.cord}
                          </td>
                          <td className="border border-black p-1" data-id="element-2988">
                            {obs.skinColour}
                          </td>
                          <td className="border border-black p-1" data-id="element-2989">
                            {obs.feedingBF} / {obs.feedingAF}
                          </td>
                          <td className="border border-black p-1" data-id="element-2990">
                            {obs.eliminationBowel}
                          </td>
                          <td className="border border-black p-1" data-id="element-2991">
                            {obs.eliminationUrine}
                          </td>
                          <td className="border border-black p-1" data-id="element-2992">
                            {obs.sign}
                          </td>
                        </tr>) : <tr data-id="element-2993">
                        <td colSpan={18} className="border border-black p-4 text-gray-400 italic" data-id="element-2994">
                          No observations recorded
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>

              {/* DRUG CHART */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-2995">
                <h3 className="font-bold uppercase underline mb-2 text-center" data-id="element-2996">
                  DRUG CHART
                </h3>
                <div className="flex justify-between mb-2 text-xs" data-id="element-2997">
                  <div data-id="element-2998">
                    <span className="font-semibold" data-id="element-2999">NAME:</span>{' '}
                    {selectedFile.patient.firstName}{' '}
                    {selectedFile.patient.surname}
                  </div>
                  <div data-id="element-3000">
                    <span className="font-semibold" data-id="element-3001">WARD:</span>{' '}
                    {selectedFile.drugChart?.ward}
                  </div>
                  <div data-id="element-3002">
                    <span className="font-semibold" data-id="element-3003">No.:</span>{' '}
                    {selectedFile.drugChart?.patientNo}
                  </div>
                  <div data-id="element-3004">
                    <span className="font-semibold" data-id="element-3005">SEX/AGE:</span> Female /{' '}
                    {selectedFile.patient.age}
                  </div>
                </div>

                <table className="w-full text-[9px] border-collapse border border-black text-center" data-id="element-3006">
                  <thead data-id="element-3007">
                    <tr className="bg-gray-100" data-id="element-3008">
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-3009">
                        DATE
                      </th>
                      <th className="border border-black p-1 w-24" rowSpan={3} data-id="element-3010">
                        DRUG
                      </th>
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-3011">
                        DOSAGE
                      </th>
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-3012">
                        ROUTE OF ADMIN
                      </th>
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-3013">
                        ORDERED BY
                      </th>
                      <th className="border border-black p-1" colSpan={21} data-id="element-3014">
                        SUPPLIED BY (DATE)
                      </th>
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-3015">
                        SUPPLIED BY
                      </th>
                      <th className="border border-black p-1 w-12" rowSpan={3} data-id="element-3016">
                        QUANTITY
                      </th>
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-3017">
                        DATE
                      </th>
                    </tr>
                    <tr className="bg-gray-100" data-id="element-3018">
                      {[0, 1, 2].map(colIdx => <th key={colIdx} className="border border-black p-1" colSpan={7} data-id="element-3019">
                          {selectedFile.drugChart?.supplyDates?.[colIdx] || ''}
                        </th>)}
                    </tr>
                    <tr className="bg-gray-100" data-id="element-3020">
                      {[0, 1, 2].map(colIdx => <Fragment key={`times-${colIdx}`} data-id="element-3021">
                          {['6', '10', '12', '14', '18', '22', '24'].map(time => <th key={`${colIdx}-${time}`} className="border border-black p-0.5 font-normal" data-id="element-3022">
                                {time}
                              </th>)}
                        </Fragment>)}
                    </tr>
                  </thead>
                  <tbody data-id="element-3023">
                    {selectedFile.drugChart?.rows && selectedFile.drugChart.rows.length > 0 ? selectedFile.drugChart.rows.map(row => <tr key={row.id} data-id="element-3024">
                          <td className="border border-black p-1" data-id="element-3025">
                            {row.date}
                          </td>
                          <td className="border border-black p-1" data-id="element-3026">
                            {row.drug}
                          </td>
                          <td className="border border-black p-1" data-id="element-3027">
                            {row.dosage}
                          </td>
                          <td className="border border-black p-1" data-id="element-3028">
                            {row.routeOfAdmin}
                          </td>
                          <td className="border border-black p-1" data-id="element-3029">
                            {row.orderedBy}
                          </td>
                          {[0, 1, 2].map(colIdx => <Fragment key={`admin-${colIdx}`} data-id="element-3030">
                              {['6', '10', '12', '14', '18', '22', '24'].map(time => {
                      const key = `${colIdx}-${time}`;
                      return <td key={key} className="border border-black p-0.5" data-id="element-3031">
                                      {row.administration?.[key] || ''}
                                    </td>;
                    })}
                            </Fragment>)}
                          <td className="border border-black p-1" data-id="element-3032">
                            {row.suppliedBy}
                          </td>
                          <td className="border border-black p-1" data-id="element-3033">
                            {row.quantity}
                          </td>
                          <td className="border border-black p-1" data-id="element-3034">
                            {row.suppliedDate}
                          </td>
                        </tr>) : <tr data-id="element-3035">
                        <td colSpan={30} className="border border-black p-4 text-gray-400 italic" data-id="element-3036">
                          No drug chart recorded
                        </td>
                      </tr>}
                  </tbody>
                </table>
                <div className="flex justify-between mt-4 text-xs" data-id="element-3037">
                  <div data-id="element-3038">
                    <span className="font-semibold" data-id="element-3039">DATE:</span>{' '}
                    {selectedFile.drugChart?.signedDate}
                  </div>
                  <div data-id="element-3040">
                    <span className="font-semibold" data-id="element-3041">SIGNATURE:</span>{' '}
                    {selectedFile.drugChart?.signedBy}
                  </div>
                </div>
              </div>

              {/* PUERPERIUM NOTES */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-3042">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-3043">
                  PUERPERIUM NOTES
                </h3>
                <table className="w-full text-sm border-collapse border border-black" data-id="element-3044">
                  <thead data-id="element-3045">
                    <tr className="bg-gray-100" data-id="element-3046">
                      <th className="border border-black p-2 text-left w-32" data-id="element-3047">
                        DATE
                      </th>
                      <th className="border border-black p-2 text-left" data-id="element-3048">
                        NOTES
                      </th>
                      <th className="border border-black p-2 text-left w-48" data-id="element-3049">
                        SIGNATURE
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-3050">
                    {selectedFile.puerperiumNotes.length > 0 ? selectedFile.puerperiumNotes.map(note => <tr key={note.id} data-id="element-3051">
                          <td className="border border-black p-2" data-id="element-3052">
                            {note.date}
                          </td>
                          <td className="border border-black p-2 whitespace-pre-wrap" data-id="element-3053">
                            {note.notes}
                          </td>
                          <td className="border border-black p-2" data-id="element-3054">
                            {note.signature}
                          </td>
                        </tr>) : <tr data-id="element-3055">
                        <td colSpan={3} className="border border-black p-4 text-gray-400 italic text-center" data-id="element-3056">
                          No notes recorded
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>

              {/* DISCHARGE SLIP */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-3057">
                <h3 className="font-bold uppercase underline mb-3 text-center" data-id="element-3058">
                  DISCHARGE SLIP FOR LABOUR WARD
                </h3>

                <div className="grid grid-cols-2 gap-x-8 gap-y-2 mb-4 text-sm" data-id="element-3059">
                  <div data-id="element-3060">
                    <span className="font-semibold" data-id="element-3061">SM No:</span>{' '}
                    {selectedFile.dischargeSlip?.smNo}
                  </div>
                  <div data-id="element-3062">
                    <span className="font-semibold" data-id="element-3063">Gravida:</span>{' '}
                    {selectedFile.dischargeSlip?.gravida}
                  </div>
                  <div data-id="element-3064">
                    <span className="font-semibold" data-id="element-3065">Patient's Name:</span>{' '}
                    {selectedFile.dischargeSlip?.patientName || `${selectedFile.patient.firstName} ${selectedFile.patient.surname}`}
                  </div>
                  <div data-id="element-3066">
                    <span className="font-semibold" data-id="element-3067">Party:</span>{' '}
                    {selectedFile.dischargeSlip?.party}
                  </div>
                  <div data-id="element-3068">
                    <span className="font-semibold" data-id="element-3069">Time of Discharge:</span>{' '}
                    {selectedFile.dischargeSlip?.timeOfDischarge}
                  </div>
                  <div data-id="element-3070">
                    <span className="font-semibold" data-id="element-3071">Age:</span>{' '}
                    {selectedFile.dischargeSlip?.age || selectedFile.patient.age}
                  </div>
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-3072">
                  Labour and Delivery Summary
                </h4>
                <div className="grid grid-cols-2 gap-x-8 gap-y-2 mb-4 text-sm border border-black p-3" data-id="element-3073">
                  <div className="font-bold uppercase" data-id="element-3074">Mother</div>
                  <div className="font-bold uppercase" data-id="element-3075">Infant</div>
                  <div data-id="element-3076">
                    <span className="font-semibold" data-id="element-3077">D.O.B:</span>{' '}
                    {selectedFile.dischargeSlip?.motherDob}
                  </div>
                  <div data-id="element-3078">
                    <span className="font-semibold" data-id="element-3079">T.O.B:</span>{' '}
                    {selectedFile.dischargeSlip?.infantTob}
                  </div>
                  <div data-id="element-3080">
                    <span className="font-semibold" data-id="element-3081">M.O.D:</span>{' '}
                    {selectedFile.dischargeSlip?.modeOfDelivery}
                  </div>
                  <div data-id="element-3082">
                    <span className="font-semibold" data-id="element-3083">A/S:</span>{' '}
                    {selectedFile.dischargeSlip?.apgarScore}
                  </div>
                  <div data-id="element-3084">
                    <span className="font-semibold" data-id="element-3085">Perineum:</span>{' '}
                    {selectedFile.dischargeSlip?.perineum}
                  </div>
                  <div data-id="element-3086">
                    <span className="font-semibold" data-id="element-3087">Sex:</span>{' '}
                    {selectedFile.dischargeSlip?.infantSex}
                  </div>
                  <div className="col-span-2" data-id="element-3088">
                    <span className="font-semibold" data-id="element-3089">Comments:</span>{' '}
                    {selectedFile.dischargeSlip?.comments}
                  </div>
                  <div className="col-span-2" data-id="element-3090">
                    <span className="font-semibold" data-id="element-3091">P.C.A:</span>{' '}
                    {selectedFile.dischargeSlip?.pca}
                  </div>
                </div>

                <div className="flex justify-between items-center mb-2" data-id="element-3092">
                  <h4 className="font-bold uppercase underline text-sm" data-id="element-3093">
                    Discharge Summary
                  </h4>
                  <div className="text-sm" data-id="element-3094">
                    <span className="font-semibold" data-id="element-3095">Date of Discharge:</span>{' '}
                    {selectedFile.dischargeSlip?.dateOfDischarge}
                  </div>
                </div>

                <div className="border border-black p-3 mb-4 text-sm" data-id="element-3096">
                  <h5 className="font-bold uppercase mb-2" data-id="element-3097">Mother</h5>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-2" data-id="element-3098">
                    <div data-id="element-3099">
                      <span className="font-semibold" data-id="element-3100">BP:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.bp}
                    </div>
                    <div data-id="element-3101">
                      <span className="font-semibold" data-id="element-3102">Pallor:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.pallor}
                    </div>
                    <div data-id="element-3103">
                      <span className="font-semibold" data-id="element-3104">Breast:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.breast}
                    </div>
                    <div data-id="element-3105">
                      <span className="font-semibold" data-id="element-3106">
                        Breastfeeding (Yes/No):
                      </span>{' '}
                      {selectedFile.dischargeSlip?.mother?.breastfeeding}
                    </div>
                    <div data-id="element-3107">
                      <span className="font-semibold" data-id="element-3108">Uterus:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.uterus}
                    </div>
                    <div data-id="element-3109">
                      <span className="font-semibold" data-id="element-3110">Perineum:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.perineum}
                    </div>
                    <div data-id="element-3111">
                      <span className="font-semibold" data-id="element-3112">Lochia:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.lochia}
                    </div>
                    <div data-id="element-3113">
                      <span className="font-semibold" data-id="element-3114">Bowels:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.bowels}
                    </div>
                    <div data-id="element-3115">
                      <span className="font-semibold" data-id="element-3116">Urine:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.urine}
                    </div>
                    <div data-id="element-3117">
                      <span className="font-semibold" data-id="element-3118">Family Planning:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.familyPlanning}
                    </div>
                    <div className="col-span-2" data-id="element-3119">
                      <span className="font-semibold" data-id="element-3120">Danger Signs:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.dangerSigns}
                    </div>
                    <div className="col-span-2" data-id="element-3121">
                      <span className="font-semibold" data-id="element-3122">Treatment:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.treatment}
                    </div>
                    <div data-id="element-3123">
                      <span className="font-semibold" data-id="element-3124">Vitamin A:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.vitaminA}
                    </div>
                  </div>
                </div>

                <div className="border border-black p-3 mb-4 text-sm" data-id="element-3125">
                  <h5 className="font-bold uppercase mb-2" data-id="element-3126">Infant</h5>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-2" data-id="element-3127">
                    <div data-id="element-3128">
                      <span className="font-semibold" data-id="element-3129">BWT:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.birthWeight}
                    </div>
                    <div data-id="element-3130">
                      <span className="font-semibold" data-id="element-3131">Discharge Weight:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.dischargeWeight}
                    </div>
                    <div data-id="element-3132">
                      <span className="font-semibold" data-id="element-3133">Pallor:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.pallor}
                    </div>
                    <div data-id="element-3134">
                      <span className="font-semibold" data-id="element-3135">Jaundice:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.jaundice}
                    </div>
                    <div className="col-span-2" data-id="element-3136">
                      <span className="font-semibold" data-id="element-3137">
                        Passed Meconium / Urine:
                      </span>{' '}
                      {selectedFile.dischargeSlip?.infant?.passedMeconiumUrine}
                    </div>
                    <div data-id="element-3138">
                      <span className="font-semibold" data-id="element-3139">Feeding:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.feeding}
                    </div>
                    <div data-id="element-3140">
                      <span className="font-semibold" data-id="element-3141">Feeding Option:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.feedingOption}
                    </div>
                    <div data-id="element-3142">
                      <span className="font-semibold" data-id="element-3143">Breast Feeding:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.breastFeeding}
                    </div>
                    <div data-id="element-3144">
                      <span className="font-semibold" data-id="element-3145">BCG:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.bcg}
                    </div>
                    <div data-id="element-3146">
                      <span className="font-semibold" data-id="element-3147">OPV:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.opv}
                    </div>
                    <div data-id="element-3148">
                      <span className="font-semibold" data-id="element-3149">Birth Record:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.birthRecord}
                    </div>
                    <div data-id="element-3150">
                      <span className="font-semibold" data-id="element-3151">G/C:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.gc}
                    </div>
                    <div className="col-span-2" data-id="element-3152">
                      <span className="font-semibold" data-id="element-3153">Treatment:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.treatment}
                    </div>
                  </div>
                </div>

                <div className="space-y-2 text-sm" data-id="element-3154">
                  <div data-id="element-3155">
                    <span className="font-semibold" data-id="element-3156">
                      6 days Postnatal Date:
                    </span>{' '}
                    {selectedFile.dischargeSlip?.sixDaysPostnatalDate}
                  </div>
                  <div data-id="element-3157">
                    <span className="font-semibold" data-id="element-3158">
                      6 Weeks Postnatal Date:
                    </span>{' '}
                    {selectedFile.dischargeSlip?.sixWeeksPostnatalDate}
                  </div>
                  <div data-id="element-3159">
                    <span className="font-semibold" data-id="element-3160">Name in Full:</span>{' '}
                    {selectedFile.dischargeSlip?.nameInFull}
                  </div>
                </div>
              </div>

              {/* POSTNATAL AT ONE WEEK */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-3161">
                <h3 className="font-bold uppercase underline mb-3 text-center" data-id="element-3162">
                  Postnatal at One Week
                </h3>
                <div className="text-sm mb-3" data-id="element-3163">
                  <span className="font-semibold" data-id="element-3164">Date:</span>{' '}
                  {selectedFile.postNatalOneWeek?.date}
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-3165">
                  Mother
                </h4>
                <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm mb-4" data-id="element-3166">
                  <div data-id="element-3167">
                    <span className="font-semibold" data-id="element-3168">General Well Being:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.generalWellBeing}
                  </div>
                  <div data-id="element-3169">
                    <span className="font-semibold" data-id="element-3170">Temp:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.temp}
                  </div>
                  <div data-id="element-3171">
                    <span className="font-semibold" data-id="element-3172">Pulse:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.pulse}
                  </div>
                  <div data-id="element-3173">
                    <span className="font-semibold" data-id="element-3174">BP:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.bp}
                  </div>
                  <div data-id="element-3175">
                    <span className="font-semibold" data-id="element-3176">Urine:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.urine}
                  </div>
                  <div data-id="element-3177">
                    <span className="font-semibold" data-id="element-3178">Head:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.head}
                  </div>
                  <div data-id="element-3179">
                    <span className="font-semibold" data-id="element-3180">Eyes:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.eyes}
                  </div>
                  <div data-id="element-3181">
                    <span className="font-semibold" data-id="element-3182">Ears:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.ears}
                  </div>
                  <div data-id="element-3183">
                    <span className="font-semibold" data-id="element-3184">Mouth:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.mouth}
                  </div>
                  <div data-id="element-3185">
                    <span className="font-semibold" data-id="element-3186">Neck:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.neck}
                  </div>
                  <div data-id="element-3187">
                    <span className="font-semibold" data-id="element-3188">Breasts:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.breasts}
                  </div>
                  <div data-id="element-3189">
                    <span className="font-semibold" data-id="element-3190">Ampills:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.ampills}
                  </div>
                  <div data-id="element-3191">
                    <span className="font-semibold" data-id="element-3192">Abdomen (Uterus):</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.abdomenUterus}
                  </div>
                  <div data-id="element-3193">
                    <span className="font-semibold" data-id="element-3194">Groin:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.groin}
                  </div>
                  <div data-id="element-3195">
                    <span className="font-semibold" data-id="element-3196">Perineum:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.perineum}
                  </div>
                  <div data-id="element-3197">
                    <span className="font-semibold" data-id="element-3198">Legs:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.legs}
                  </div>
                  <div data-id="element-3199">
                    <span className="font-semibold" data-id="element-3200">Anus:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.anus}
                  </div>
                  <div data-id="element-3201">
                    <span className="font-semibold" data-id="element-3202">Lochia:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.lochia}
                  </div>
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-3203">
                  Vaginal Examination
                </h4>
                <div className="space-y-2 text-sm mb-4" data-id="element-3204">
                  <div data-id="element-3205">
                    <span className="font-semibold" data-id="element-3206">Cervix per speculum:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-3207">
                      {selectedFile.postNatalOneWeek?.mother.cervixPerSpeculum}
                    </span>
                  </div>
                  <div data-id="element-3208">
                    <span className="font-semibold" data-id="element-3209">Family Planning:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-3210">
                      {selectedFile.postNatalOneWeek?.mother.familyPlanning}
                    </span>
                  </div>
                  <div data-id="element-3211">
                    <span className="font-semibold" data-id="element-3212">Treatment or advise:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-3213">
                      {selectedFile.postNatalOneWeek?.mother.treatmentOrAdvise}
                    </span>
                  </div>
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-3214">
                  Infant Examination Form
                </h4>
                <div className="grid grid-cols-3 gap-x-8 gap-y-1 text-sm mb-3" data-id="element-3215">
                  <div data-id="element-3216">
                    <span className="font-semibold" data-id="element-3217">Infant:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.name}
                  </div>
                  <div data-id="element-3218">
                    <span className="font-semibold" data-id="element-3219">Weight:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.weight}
                  </div>
                  <div data-id="element-3220">
                    <span className="font-semibold" data-id="element-3221">Date:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.date}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm mb-3" data-id="element-3222">
                  <div data-id="element-3223">
                    <span className="font-semibold" data-id="element-3224">Head Circumference:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.headCircumference}
                  </div>
                  <div data-id="element-3225">
                    <span className="font-semibold" data-id="element-3226">Length:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.length}
                  </div>
                  <div data-id="element-3227">
                    <span className="font-semibold" data-id="element-3228">Temp:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.temp}
                  </div>
                  <div data-id="element-3229">
                    <span className="font-semibold" data-id="element-3230">Head:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.head}
                  </div>
                  <div data-id="element-3231">
                    <span className="font-semibold" data-id="element-3232">Eyes:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.eyes}
                  </div>
                  <div data-id="element-3233">
                    <span className="font-semibold" data-id="element-3234">Ears:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.ears}
                  </div>
                  <div data-id="element-3235">
                    <span className="font-semibold" data-id="element-3236">Mouth:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.mouth}
                  </div>
                  <div data-id="element-3237">
                    <span className="font-semibold" data-id="element-3238">Neck:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.neck}
                  </div>
                  <div data-id="element-3239">
                    <span className="font-semibold" data-id="element-3240">Chest:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.chest}
                  </div>
                  <div data-id="element-3241">
                    <span className="font-semibold" data-id="element-3242">Abdomen:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.abdomen}
                  </div>
                  <div data-id="element-3243">
                    <span className="font-semibold" data-id="element-3244">Cord:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.cord}
                  </div>
                  <div data-id="element-3245">
                    <span className="font-semibold" data-id="element-3246">Genitalia & Rectum:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.genitaliaRectum}
                  </div>
                  <div data-id="element-3247">
                    <span className="font-semibold" data-id="element-3248">Extremities:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.extremities}
                  </div>
                  <div data-id="element-3249">
                    <span className="font-semibold" data-id="element-3250">Back:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.back}
                  </div>
                  <div data-id="element-3251">
                    <span className="font-semibold" data-id="element-3252">Neurologic Exam:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.neurologicExam}
                  </div>
                  <div data-id="element-3253">
                    <span className="font-semibold" data-id="element-3254">Feeding:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.feeding}
                  </div>
                </div>
                <div className="text-sm" data-id="element-3255">
                  <span className="font-semibold" data-id="element-3256">Additional Notes:</span>
                  <p className="whitespace-pre-wrap mt-1" data-id="element-3257">
                    {selectedFile.postNatalOneWeek?.infant.additionalNotes}
                  </p>
                </div>
              </div>

              {/* POSTNATAL AT SIX WEEKS */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-3258">
                <h3 className="font-bold uppercase underline mb-3 text-center" data-id="element-3259">
                  Postnatal at Six Weeks
                </h3>
                <div className="text-sm mb-3" data-id="element-3260">
                  <span className="font-semibold" data-id="element-3261">Date:</span>{' '}
                  {selectedFile.postNatalSixWeeks?.date}
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-3262">
                  Mother
                </h4>
                <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm mb-4" data-id="element-3263">
                  <div data-id="element-3264">
                    <span className="font-semibold" data-id="element-3265">General Well Being:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.generalWellBeing}
                  </div>
                  <div data-id="element-3266">
                    <span className="font-semibold" data-id="element-3267">Temp:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.temp}
                  </div>
                  <div data-id="element-3268">
                    <span className="font-semibold" data-id="element-3269">Pulse:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.pulse}
                  </div>
                  <div data-id="element-3270">
                    <span className="font-semibold" data-id="element-3271">BP:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.bp}
                  </div>
                  <div data-id="element-3272">
                    <span className="font-semibold" data-id="element-3273">Urine:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.urine}
                  </div>
                  <div data-id="element-3274">
                    <span className="font-semibold" data-id="element-3275">Head:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.head}
                  </div>
                  <div data-id="element-3276">
                    <span className="font-semibold" data-id="element-3277">Eyes:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.eyes}
                  </div>
                  <div data-id="element-3278">
                    <span className="font-semibold" data-id="element-3279">Ears:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.ears}
                  </div>
                  <div data-id="element-3280">
                    <span className="font-semibold" data-id="element-3281">Mouth:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.mouth}
                  </div>
                  <div data-id="element-3282">
                    <span className="font-semibold" data-id="element-3283">Neck:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.neck}
                  </div>
                  <div data-id="element-3284">
                    <span className="font-semibold" data-id="element-3285">Breasts:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.breasts}
                  </div>
                  <div data-id="element-3286">
                    <span className="font-semibold" data-id="element-3287">Ampills:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.ampills}
                  </div>
                  <div data-id="element-3288">
                    <span className="font-semibold" data-id="element-3289">Abdomen (Uterus):</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.abdomenUterus}
                  </div>
                  <div data-id="element-3290">
                    <span className="font-semibold" data-id="element-3291">Groin:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.groin}
                  </div>
                  <div data-id="element-3292">
                    <span className="font-semibold" data-id="element-3293">Perineum:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.perineum}
                  </div>
                  <div data-id="element-3294">
                    <span className="font-semibold" data-id="element-3295">Legs:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.legs}
                  </div>
                  <div data-id="element-3296">
                    <span className="font-semibold" data-id="element-3297">Anus:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.anus}
                  </div>
                  <div data-id="element-3298">
                    <span className="font-semibold" data-id="element-3299">Lochia:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.lochia}
                  </div>
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-3300">
                  Vaginal Examination
                </h4>
                <div className="space-y-2 text-sm mb-4" data-id="element-3301">
                  <div data-id="element-3302">
                    <span className="font-semibold" data-id="element-3303">Cervix per speculum:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-3304">
                      {selectedFile.postNatalSixWeeks?.mother.cervixPerSpeculum}
                    </span>
                  </div>
                  <div data-id="element-3305">
                    <span className="font-semibold" data-id="element-3306">Family Planning:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-3307">
                      {selectedFile.postNatalSixWeeks?.mother.familyPlanning}
                    </span>
                  </div>
                  <div data-id="element-3308">
                    <span className="font-semibold" data-id="element-3309">Treatment or advise:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-3310">
                      {selectedFile.postNatalSixWeeks?.mother.treatmentOrAdvise}
                    </span>
                  </div>
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-3311">
                  Infant Examination Form
                </h4>
                <div className="grid grid-cols-3 gap-x-8 gap-y-1 text-sm mb-3" data-id="element-3312">
                  <div data-id="element-3313">
                    <span className="font-semibold" data-id="element-3314">Infant:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.name}
                  </div>
                  <div data-id="element-3315">
                    <span className="font-semibold" data-id="element-3316">Weight:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.weight}
                  </div>
                  <div data-id="element-3317">
                    <span className="font-semibold" data-id="element-3318">Date:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.date}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm mb-3" data-id="element-3319">
                  <div data-id="element-3320">
                    <span className="font-semibold" data-id="element-3321">Head Circumference:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.headCircumference}
                  </div>
                  <div data-id="element-3322">
                    <span className="font-semibold" data-id="element-3323">Length:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.length}
                  </div>
                  <div data-id="element-3324">
                    <span className="font-semibold" data-id="element-3325">Temp:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.temp}
                  </div>
                  <div data-id="element-3326">
                    <span className="font-semibold" data-id="element-3327">Head:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.head}
                  </div>
                  <div data-id="element-3328">
                    <span className="font-semibold" data-id="element-3329">Eyes:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.eyes}
                  </div>
                  <div data-id="element-3330">
                    <span className="font-semibold" data-id="element-3331">Ears:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.ears}
                  </div>
                  <div data-id="element-3332">
                    <span className="font-semibold" data-id="element-3333">Mouth:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.mouth}
                  </div>
                  <div data-id="element-3334">
                    <span className="font-semibold" data-id="element-3335">Neck:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.neck}
                  </div>
                  <div data-id="element-3336">
                    <span className="font-semibold" data-id="element-3337">Chest:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.chest}
                  </div>
                  <div data-id="element-3338">
                    <span className="font-semibold" data-id="element-3339">Abdomen:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.abdomen}
                  </div>
                  <div data-id="element-3340">
                    <span className="font-semibold" data-id="element-3341">Cord:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.cord}
                  </div>
                  <div data-id="element-3342">
                    <span className="font-semibold" data-id="element-3343">Genitalia & Rectum:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.genitaliaRectum}
                  </div>
                  <div data-id="element-3344">
                    <span className="font-semibold" data-id="element-3345">Extremities:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.extremities}
                  </div>
                  <div data-id="element-3346">
                    <span className="font-semibold" data-id="element-3347">Back:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.back}
                  </div>
                  <div data-id="element-3348">
                    <span className="font-semibold" data-id="element-3349">Neurologic Exam:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.neurologicExam}
                  </div>
                  <div data-id="element-3350">
                    <span className="font-semibold" data-id="element-3351">Feeding:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.feeding}
                  </div>
                </div>
                <div className="text-sm" data-id="element-3352">
                  <span className="font-semibold" data-id="element-3353">Additional Notes:</span>
                  <p className="whitespace-pre-wrap mt-1" data-id="element-3354">
                    {selectedFile.postNatalSixWeeks?.infant.additionalNotes}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </div>;
};